/**
 * Изолированный стор-синглтон прогресса загрузки.
 *
 * Два независимых источника «занятости», объединяемых в один булев снимок:
 *
 * 1. Навигация — модель «цель против отрисованного»: `target` — путь, на который
 *    идёт переход (ставится сразу при смене URL), `committed` — путь, который
 *    реально отрисован (ставится, когда новый роут смонтирован). Пока они не
 *    равны — идёт переход.
 * 2. Ручные задачи — счётчик `taskCount`: opt-in-механизм зажечь ту же полосу
 *    под конкретное API-действие (например, смену значения в выпадающем списке),
 *    не связанное с навигацией.
 *
 * Снимок: `isLoading = (target !== committed) || taskCount > 0`.
 *
 * Состояние живёт вне React и Redux: подписка — через `useSyncExternalStore`,
 * поэтому ре-рендерится только сам индикатор и только при смене idle↔loading.
 */

type Listener = () => void;

interface INavProgressState {
  target: string;
  committed: string;
  taskCount: number;
}

const initialPath =
  typeof window !== 'undefined' ? window.location.pathname : '';

const state: INavProgressState = {
  target: initialPath,
  committed: initialPath,
  taskCount: 0
};

const listeners = new Set<Listener>();

/** Кэш булева снимка — для стабильной ссылки в useSyncExternalStore. */
let snapshot = false;

const computeSnapshot = (): boolean =>
  state.target !== state.committed || state.taskCount > 0;

const emit = (): void => {
  const next = computeSnapshot();
  if (next === snapshot) {
    return;
  }
  snapshot = next;
  listeners.forEach((listener) => {
    listener();
  });
};

/** Начало перехода: фиксируем целевой путь (вызывается при смене URL). */
export const notifyNavigation = (pathname: string): void => {
  state.target = pathname;
  emit();
};

/** Роут отрисован: фиксируем фактически смонтированный путь. */
export const notifyCommitted = (pathname: string): void => {
  state.committed = pathname;
  emit();
};

/**
 * Начало ручной задачи загрузки (не связанной с навигацией). Инкрементит счётчик
 * активных задач и возвращает идемпотентную функцию «стоп» — её безопасно вызвать
 * повторно, декремент произойдёт лишь один раз.
 */
export const startGlobalTask = (): (() => void) => {
  state.taskCount += 1;
  emit();

  let stopped = false;
  return () => {
    if (stopped) {
      return;
    }
    stopped = true;
    state.taskCount = Math.max(0, state.taskCount - 1);
    emit();
  };
};

/** Подписка для useSyncExternalStore. Возвращает функцию отписки. */
export const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Текущий снимок: идёт ли переход между страницами. */
export const getSnapshot = (): boolean => snapshot;
